import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"NUXT_ENV_VERCEL_GIT_PROVIDER":"github","NUXT_ENV_VERCEL_GIT_PULL_REQUEST_ID":"","NUXT_ENV_VERCEL_URL":"whitelabel-checkout-gls8f224r-white-label.vercel.app","NUXT_ENV_VERCEL_ENV":"production","NUXT_ENV_VERCEL_GIT_REPO_OWNER":"unico-pag","NUXT_ENV_VERCEL_GIT_COMMIT_MESSAGE":"Merge pull request #232 from unico-pag/fix/change-conditional-to-3ds-acquirer-works\n\nfix(payment): update 3DS acquirer conditional logic to ensure correct…","NUXT_ENV_VERCEL_BRANCH_URL":"whitelabel-checkout-git-master-white-label.vercel.app","NUXT_ENV_VERCEL_GIT_COMMIT_AUTHOR_LOGIN":"macerd","NUXT_ENV_VERCEL_TARGET_ENV":"production","NUXT_ENV_VERCEL_GIT_REPO_SLUG":"unico-checkout","NUXT_ENV_VERCEL_GIT_COMMIT_REF":"master","NUXT_ENV_VERCEL_DEPLOYMENT_ID":"dpl_55W4yXmXDbbpLtuces3bv8VvDRqp","NUXT_ENV_VERCEL_PROJECT_PRODUCTION_URL":"brasilange.shop","NUXT_ENV_VERCEL_GIT_COMMIT_SHA":"7c2cc5b09b4bfeea74b51619a03f08a6439db7b1","NUXT_ENV_VERCEL_GIT_PREVIOUS_SHA":"","NUXT_ENV_VERCEL_GIT_COMMIT_AUTHOR_NAME":"Emanuel Lacerda","NUXT_ENV_VERCEL_GIT_REPO_ID":"629031115","NUXT_ENV_VERCEL_PROJECT_ID":"prj_NuqA0WEsNHNMmeY8xfgu0WHjEwsn","BASE_URL_API":"https://api.unicopag.com.br/api/api","URL_CHECKOUT":"https://pay.unicopag.com.br","CHECKOUT_INTEREST":"2.99","VERCEL_EDGE_SUSPENSE_CACHE":"1","VERCEL_HIVE_VERSION":"fc0a4cfec5f5ceef427d19b32ffcf363818672c0\n","VERCEL_RICHER_DEPLOYMENT_OUTPUTS":"1","VERCEL_GIT_PULL_REQUEST_ID":"","VERCEL_EDGE_FUNCTIONS_EMBEDDED_SOURCEMAPS":"1","VERCEL_EDGE_FUNCTIONS_MAX_DURATION":"300","VERCEL_BUILD_IMAGE":"al2023","AWS_EXECUTION_ENV":"vercel-hive","VERCEL_GIT_COMMIT_REF":"master","npm_package_scripts_analyze":"nuxt build --analyze","npm_package_devDependencies__chenfengyuan_vue_countdown":"1.1.5","VERCEL_USE_SQUASHFS_BUILD_CACHE":"1","VERCEL_API_BUILD_CONTAINERS_ENDPOINT":"https://api-iad1.vercel.com/build-containers","USE_RUST_EDGE_WORKER_LAYER_LATEST":"1","VERCEL_EDGE_FNS_ON_SERVERLESS_USE_FILESYSTEM_CONTENT":"1","VERCEL_EDGE_FUNCTIONS_USE_FILESYSTEM_CONTENT":"1","NODE":"/node20/bin/node","VERCEL_EDGE_FNS_ON_WORKERD":"1","npm_config_ignore_scripts":"","NODE_OPTIONS":"--max_old_space_size=8192","VERCEL_EDGE_MIDDLEWARE_ON_SERVERLESS_NODE_NO_CF_DEPLOY":"1","USE_OUTPUT_FOR_EDGE_FUNCTIONS":"1","npm_package_dependencies_vue_cookie_next":"^1.2.1","VERCEL_ALWAYS_OBTAIN_IN_INIT":"1","npm_package_devDependencies_autoprefixer":"^10.4.13","VERCEL_EDGE_MIDDLEWARE_MEMORY_SIZE":"1024","npm_package_devDependencies_tailwindcss":"^3.2.4","USE_RUST_LAYER_LATEST":"1","npm_package_dependencies__bugsnag_browser_performance":"^2.10.1","npm_package_private":"true","npm_package_dependencies__bugsnag_js":"^8.1.2","npm_package_devDependencies_nuxt":"^2.17.2","VERCEL_ARTIFACTS_OWNER":"team_3Cnt4V2ouiyIZiAMZccR4fNd","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"run\",\"build\"]}","VERCEL_ENABLE_IGNORE_BUILD_STEP_WITH_SQUASHFS":"1","npm_config_bin_links":"true","VERCEL_ROOT_CONTAINER_POLL_TIME":"1750457912633","npm_package_dependencies_axios":"^1.8.2","PWD":"/vercel/path0","npm_config_save_prefix":"^","VERCEL_BRANCH_URL":"whitelabel-checkout-git-master-white-label.vercel.app","VERCEL_TARGET_ENV":"production","npm_package_readmeFilename":"README.md","npm_package_devDependencies_sweetalert2":"^11.6.10","npm_package_devDependencies__babel_core":"^7.19.6","npm_config_store_dir":"/vercel/path0/node_modules/.pnpm-store","npm_package_scripts_build":"nuxt build","npm_package_devDependencies__nuxtjs_axios":"^5.13.6","npm_config_network_timeout":"100000","_":"/vercel/path0/node_modules/.bin/nuxt","npm_package_devDependencies__faker_js_faker":"^8.4.1","VERCEL_TELEMETRY_DISABLED":"1","FAKEROOTKEY":"yes","TZ":":UTC","VERCEL_ENABLE_NODE_COMPATIBILITY":"1","VERCEL_USE_STREAMING_PRERENDER":"1","VERCEL_ORG_ID":"team_3Cnt4V2ouiyIZiAMZccR4fNd","HOME":"/vercel","VERCEL_GIT_REPO_SLUG":"unico-checkout","VERCEL_ENV":"production","npm_config_version_git_tag":"true","LANG":"en_US.UTF-8","VERCEL_GIT_PREVIOUS_SHA":"","VERCEL_URL":"whitelabel-checkout-gls8f224r-white-label.vercel.app","npm_config_init_license":"MIT","YARN_NODE_LINKER":"node-modules","VERCEL_STORE_SOURCE_DEPLOYMENT_ON_CACHE":"1","npm_package_version":"1.0.4","npm_package_dependencies_vercel":"^32.7.0","VERCEL_GIT_COMMIT_AUTHOR_LOGIN":"macerd","VERCEL_CONTAINER_START_TIME":"1750457881505","VERCEL_PRERENDER_METADATA_AS_METADATA_FILE":"1","VERCEL_USE_BYTECODE_CACHING":"1","VERCEL_SERVERLESS_FUNCTION_FAILOVER":"1","npm_config_version_commit_hooks":"true","VERCEL_BUILD_MONOREPO_SUPPORT":"1","npm_package_dependencies_crypto_js":"^4.2.0","VERCEL_DISCOVER_FOLDER_SIZES":"1","npm_package_scripts_cy_open":"cypress open","VERCEL_CLUSTER":"hvi_iad1_fern","VERCEL_PROJECT_ID":"prj_NuqA0WEsNHNMmeY8xfgu0WHjEwsn","VERCEL_EDGE_ON_SERVERLESS_NODE":"1","npm_package_dependencies_styled_components":"^6.0.0-rc.3","npm_package_devDependencies_vue_the_mask":"^0.11.1","VERCEL_ENABLE_EXTENDED_FALLBACK_PAYLOAD":"1","NEXT_SHARP_PATH":"/usr/local/share/.config/yarn/global/node_modules/sharp","INIT_CWD":"/vercel/path0","VERCEL_GIT_COMMIT_MESSAGE":"Merge pull request #232 from unico-pag/fix/change-conditional-to-3ds-acquirer-works\n\nfix(payment): update 3DS acquirer conditional logic to ensure correct…","VERCEL_PROJECT_PRODUCTION_URL":"brasilange.shop","NOW_BUILDER":"1","npm_package_devDependencies_nuxt_svg_loader":"^1.2.0","TURBO_DOWNLOAD_LOCAL_ENABLED":"true","npm_lifecycle_script":"nuxt build","npm_package_description":"Checkout Único Drop","VERCEL_SERVERLESS_SUSPENSE_CACHE":"1","DD_TRACE_STARTUP_LOGS":"false","npm_package_dependencies_pusher_js":"^8.4.0-rc2","npm_config_version_tag_prefix":"v","VERCEL_HIVE_ID":"hvi_iad1_fern","YARN_WRAP_OUTPUT":"false","VERCEL_BUILD_PROVIDER":"hive-env","VERCEL_COMPRESS_SERVERLESS_RESPONSE":"1","VERCEL_GIT_REPO_ID":"629031115","NX_DAEMON":"false","npm_package_devDependencies_cypress":"^13.8.1","VERCEL_EDGE_FUNCTIONS_MEMORY_SIZE":"1769","VERCEL_OIDC_TOKEN":"eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Im1yay00MzAyZWMxYjY3MGY0OGE5OGFkNjFkYWRlNGEyM2JlNyJ9.eyJpc3MiOiJodHRwczovL29pZGMudmVyY2VsLmNvbS93aGl0ZS1sYWJlbCIsInN1YiI6Im93bmVyOndoaXRlLWxhYmVsOnByb2plY3Q6d2hpdGVsYWJlbC1jaGVja291dDplbnZpcm9ubWVudDpwcm9kdWN0aW9uIiwic2NvcGUiOiJvd25lcjp3aGl0ZS1sYWJlbDpwcm9qZWN0OndoaXRlbGFiZWwtY2hlY2tvdXQ6ZW52aXJvbm1lbnQ6cHJvZHVjdGlvbiIsImF1ZCI6Imh0dHBzOi8vdmVyY2VsLmNvbS93aGl0ZS1sYWJlbCIsIm93bmVyIjoid2hpdGUtbGFiZWwiLCJvd25lcl9pZCI6InRlYW1fM0NudDRWMm91aXlJWmlBTVpjY1I0Zk5kIiwicHJvamVjdCI6IndoaXRlbGFiZWwtY2hlY2tvdXQiLCJwcm9qZWN0X2lkIjoicHJqX051cUEwV0VzTkhOTW1lWTh4Zmd1MFdIakV3c24iLCJlbnZpcm9ubWVudCI6InByb2R1Y3Rpb24iLCJuYmYiOjE3NTA0NTc5MDksImlhdCI6MTc1MDQ1NzkwOSwiZXhwIjoxNzUwNDYxNTA5fQ.P8ofQQcs5GDVgOco7qn5w8g2b3xiB2no68eKJUbKQw1_8PMHzbcqKemj_yotd5tPnurZ3gfgEnSKT8D3UmWgTO9HNbUA8z96QimsAW0V9c7VdNbpTJhJWsIFUH-YQvggAb4ZyFnuGnfYIHVix3qGsSkDWsnvaH235E88fBFRKnydxl1LkBWCQty8cfhJk6OFkUVEa2FA93T1y1v-Hy20MQoxMm1-hiJt7SJ-wKV1UyHTIbhdZhchShTxuZy_2UQMcsxDXTWOUL5LFsPe0jd7WPiX6COlXCyuH2d8mIezIH_V0EszaQ-tpVnOc_d2euF2P7LDle4-zOq_aFnV6L8ndw","VERCEL_EDGE_OTEL_COLLECTOR":"1","npm_package_name":"tailwind_landing_nuxt","npm_package_dependencies__bugsnag_plugin_vue":"^8.1.1","PUSHER_APP_KEY":"228dae4406a11a7c2d38","TURBO_CACHE":"remote:rw","VERCEL_RETRY_ON_MISSING_RESPONSE_BATON":"1","npm_package_devDependencies__tailwindcss_forms":"^0.5.3","VERCEL_DEPLOYMENT_ROUTES_BUILD_OUTPUT_V1":"1","VERCEL_ENABLE_REGIONALIZED_ISR":"1","VERCEL_GIT_REPO_OWNER":"unico-pag","npm_package_dependencies_qrcode":"^1.5.3","VERCEL_FUNCTION_REGIONS":"iad1","npm_package_dependencies_dotenv":"^16.3.1","npm_lifecycle_event":"build","SHLVL":"0","npm_config_version_git_sign":"","npm_package_scripts_deploy":"set NODE_ENV=production&& nuxt generate && git add . && git commit -m 'production' && git push origin master","npm_config_version_git_message":"v%s","npm_package_devDependencies_posthog_js":"^1.98.2","npm_package_devDependencies__nuxtjs_dotenv":"^1.4.1","npm_package_devDependencies_vue_qrcode_component":"^2.1.1","VERCEL_ENABLE_FUNCTION_WARMING":"1","TURBO_CI_VENDOR_ENV_KEY":"NUXT_ENV_VERCEL_","VERCEL_EDGE_FNS_ON_SERVERLESS":"1","VERCEL_PROJECT_SETTINGS_NODE_VERSION":"20.x","VERCEL_EDGE_FUNCTIONS_STRICT_MODE":"1","VERCEL_ARTIFACTS_TOKEN":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0eXBlIjoidGFzay1ydW5uZXIiLCJ1c2VySWQiOiI3WVY2TkJMcGk2V21tTEpTQUlRekdsTmgiLCJjYXBhYmlsaXRpZXMiOlsiQVBJX0FSVElGQUNUU19VUExPQUQiLCJBUElfQVJUSUZBQ1RTX0RPV05MT0FEIiwiQVBJX0FSVElGQUNUU19FWElTVFMiLCJBUElfQVJUSUZBQ1RTX1FVRVJZIiwiQVBJX0FSVElGQUNUU19FVkVOVCIsIkFQSV9TUEFDRVNfUlVOX1VQTE9BRCJdLCJkYXRhIjp7InByb2plY3RJZCI6InByal9OdXFBMFdFc05ITk1tZVk4eGZndTBXSGpFd3NuIn0sImlhdCI6MTc1MDQ1NzkxMCwiZXhwIjoxNzUwNDU5NzEwfQ.6IL2kTM16ZEUvUeetx0tn3eqjNxOBN8uXOKYXA0dZEw","VERCEL_GIT_COMMIT_AUTHOR_NAME":"Emanuel Lacerda","TURBO_REMOTE_ONLY":"true","npm_package_devDependencies_sass_loader":"10","npm_config_user_agent":"yarn/1.22.19 npm/? node/v20.19.2 linux x64","VERCEL_SET_REQUESTED_AT_PRODUCTION_ON_DEPLOYMENT":"1","npm_package_dependencies__nuxtjs_gtm":"^2.4.0","VERCEL_DEPLOYMENT_ROUTES_CLEAN":"1","VERCEL":"1","npm_execpath":"/yarn1/node_modules/yarn/bin/yarn.js","npm_config_strict_ssl":"true","VERCEL_GIT_COMMIT_SHA":"7c2cc5b09b4bfeea74b51619a03f08a6439db7b1","VERCEL_NEXT_BUNDLED_SERVER":"1","npm_package_devDependencies__kouts_vue_modal":"^2.1.11","DD_TAGS":"ec2_host:i-006ad5c57917d7a5a","npm_package_scripts_dev":"nuxt","VERCEL_LAMBDA_OUTPUTS_AS_MIDDLEWARE":"1","VERCEL_GIT_PROVIDER":"github","VERCEL_EDGE_FNS_ON_WORKERD_UNBUNDLED_FORMAT":"1","TURBO_RUN_SUMMARY":"true","TURBO_PLATFORM_ENV":"BASE_URL_API,URL_CHECKOUT,CHECKOUT_INTEREST,PUSHER_APP_KEY","VERCEL_COMPRESSED_ISR_BILLING":"1","npm_package_scripts_start":"nuxt start","npm_config_node_linker":"node-modules","PATH":"/tmp/yarn--1750457955168-0.03310905175645473:/vercel/path0/node_modules/.bin:/vercel/.config/yarn/link/node_modules/.bin:/node20/libexec/lib/node_modules/npm/bin/node-gyp-bin:/node20/lib/node_modules/npm/bin/node-gyp-bin:/node20/bin/node_modules/npm/bin/node-gyp-bin:/vercel/path0/node_modules/.bin:/pnpm6/node_modules/.bin:/yarn1/node_modules/yarn/bin:/vercel/.config/yarn/global/node_modules/.bin:/ruby33/bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/bun1:/node20/bin","VERCEL_API_ENDPOINT":"https://api-iad1.vercel.com","NPM_CONFIG_STORE_DIR":"/vercel/path0/node_modules/.pnpm-store","CI":"1","__VERCEL_BUILD_RUNNING":"1","npm_package_devDependencies_sass":"^1.62.1","npm_package_license":"MIT","VERCEL_IMAGE_ID":"sha256:7ff5f67390f044539a222e634575235a49e805370673049854ad6fcfa547f31a","npm_package_devDependencies_cpf_cnpj_validator":"^1.0.3","VERCEL_NODE_BRIDGE_COMPRESS_MULTI_PAYLOADS":"1","npm_config_registry":"https://registry.yarnpkg.com","NEXT_PRIVATE_MULTI_PAYLOAD":"1","npm_config_ignore_optional":"","VERCEL_USE_ONLY_STREAMING_LAMBDA":"1","VERCEL_DEPLOYMENT_ID":"dpl_55W4yXmXDbbpLtuces3bv8VvDRqp","npm_package_devDependencies_laravel_echo":"^1.16.1","npm_package_devDependencies__nuxtjs_google_gtag":"^1.0.4","npm_node_execpath":"/node20/bin/node","VERCEL_FORCE_EDGE_FUNCTIONS_REDEPLOY":"1","npm_package_scripts_generate":"nuxt generate","VERCEL_WAKE_UP_DEPLOYMENT":"1","VERCEL_EDGE_FUNCTIONS_REGIONAL_INVOCATION":"1","npm_package_devDependencies__nuxt_postcss8":"^1.1.3","VERCEL_HIVE_CELL_ID":"hvc_dd482e853fba4199a8a83aa4143e","VERCEL_BUILD_OUTPUTS_EDGE_FUNCTION":"1","VERCEL_API_BUILD_CONTAINERS_TOKEN":"co15Hx4uUK9TGXJc4Odq34wR","npm_config_init_version":"1.0.0","NODE_ENV":"production","_applied":"true","VUE_ENV":"server","_AXIOS_BASE_URL_":"https://api.whitelabel-up.com/api/api"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
